import { UserOutlined } from '@ant-design/icons'
import { useQuery } from '@apollo/client'
import { Select, SelectProps } from 'antd'
import { debounce, uniqBy } from 'lodash'
import { useEffect, useState } from 'react'
import { PERSONNE_SEARCH_BY_COPRO_QUERY } from '../apollo/query/personne.query'
import { COLOR } from '../constants'
import { useSearchPersonneByCopro } from '../lib/hooks'
import { D4_Lot } from '../types'

const formatLots = (lots: D4_Lot[]) => {
  return (
    lots.map((lot) => ({
      label: [
        <UserOutlined style={{ marginRight: '5px', color: COLOR.blue }} />,
        `${lot.copropriete?.trigramme} - ${
          lot.lot_coproprietaire?.coproprietaire?.personne
            ? `${lot.lot_coproprietaire?.coproprietaire?.personne?.nom} - ${lot.lot_coproprietaire?.coproprietaire?.personne?.prenom}`
            : lot.lot_coproprietaire?.coproprietaire?.societe?.raisonsociale
        }`,
      ],
      value: lot.lot_coproprietaire?.coproprietaire?.id,
    })) ?? []
  )
}

const SelectCoproprietaires: React.FC<
  Omit<SelectProps<any>, 'onChange'> & {
    defaultValue?: number
    trigramme: string
    onChange: (value: any, option: D4_Lot) => void
  }
> = ({ trigramme, defaultValue, ...selectProps }) => {
  const search = useSearchPersonneByCopro(trigramme)
  const [options, setOptions] = useState<Array<any>>([])
  const [lots, setLots] = useState<D4_Lot[]>()

  const { data, loading } = useQuery<{ lots: D4_Lot[] }, { search: string; id: number; trigramme: string }>(
    PERSONNE_SEARCH_BY_COPRO_QUERY,
    {
      variables: {
        id: defaultValue ?? -1,
        trigramme,
        search: '',
      },
    }
  )

  useEffect(() => {
    if (data?.lots) {
      setOptions((opt) => opt.concat(formatLots(data.lots)))
    }
  }, [data])

  const handleSearch = debounce(async (text: string) => {
    const res = await search({ search: text })
    const lots = uniqBy(res?.data.lots, 'lot_coproprietaire.coproprietaire.id')
    const personneOptions = formatLots(lots)

    setLots(lots)
    setOptions(personneOptions)
  }, 300)

  const handleChange = (value: any, option: any) => {
    const lot = lots?.find((lot) => lot.lot_coproprietaire?.coproprietaire?.id === value)
    selectProps.onChange?.(value, lot as D4_Lot)
  }

  if (loading) {
    return null
  }

  return (
    <Select
      {...selectProps}
      onSearch={handleSearch}
      showSearch
      options={selectProps.options ? selectProps.options?.concat(options) : options}
      optionFilterProp="label"
      onChange={handleChange}
      defaultValue={
        data?.lots?.[0]?.lot_coproprietaire?.coproprietaire?.personne?.id ??
        data?.lots?.[0]?.lot_coproprietaire?.coproprietaire?.societe?.id ??
        defaultValue
      }
    />
  )
}

export default SelectCoproprietaires
